import { useSenderAccountContext } from "context/SenderAccountContext";
import { DOCS_FORM_NAMES, REHAB_FORM_NAMES } from "core/consts";
import { getIsDtaActive } from "core/model/productfeatures/utils";
import {
  Auction,
  GetOntologyType,
  GKVGeneralFormData,
  GKVMedicalFormData,
  Patient,
  RehabForms,
  TransitionalCareFormData,
  ValueOf,
} from "core/types";
import { DRVGeneralFormData } from "dsl/ecosystems/PatientForms/RehabForms/DRVGeneralForm/modelDefinition";
import { DRVMedicalFormData } from "dsl/ecosystems/PatientForms/RehabForms/DRVMedicalForm/modelDefinition";
import { Dispatch, ReactNode, SetStateAction } from "react";
import Translations from "translations/types";
import { z } from "zod";
import { getGKVGeneralFormConfig } from "./GKVGeneral";

type DocsFormName = ValueOf<typeof DOCS_FORM_NAMES>;

type DocsFormTypeMapping = {
  [DOCS_FORM_NAMES.DRV_MEDICAL]: DRVMedicalFormData;
  [DOCS_FORM_NAMES.DRV_GENERAL]: DRVGeneralFormData;
  [DOCS_FORM_NAMES.GKV_MEDICAL]: GKVMedicalFormData;
  [DOCS_FORM_NAMES.GKV_GENERAL]: GKVGeneralFormData;
  [DOCS_FORM_NAMES.TRANSITIONAL_CARE]: TransitionalCareFormData;
};

export type DocsFormDataMapped<T extends DocsFormName> = DocsFormTypeMapping[T];

export const MIGRATED_FORM_TYPES = [DOCS_FORM_NAMES.GKV_GENERAL] as const;

export type MigratedFormTypes = (typeof MIGRATED_FORM_TYPES)[number];

export type DocsFormFormGetters<T extends DocsFormName> = Partial<
  Record<
    keyof DocsFormDataMapped<T>,
    (auction: Auction) => ValueOf<DocsFormDataMapped<T>> | null
  >
>;

export type FormFieldConfigProps<T extends MigratedFormTypes> = {
  auction: Auction;
  skipRequiredValidation: boolean;
  translations: Translations;
  setSourceSelected: Dispatch<
    SetStateAction<keyof DocsFormDataMapped<T> | undefined>
  >;
  sourceSelected: keyof DocsFormDataMapped<T> | undefined;
};

export type FormFieldConfig<T extends MigratedFormTypes> = {
  schema: z.ZodSchema<DocsFormDataMapped<T>>;
  title: (props: { translations: Translations }) => string;
  auctionToForm: (
    getOntology: GetOntologyType,
    translations: Translations,
  ) => DocsFormFormGetters<T>;
  getInitialValue: (props: {
    forms: RehabForms;
    patient: Patient;
    value?: DocsFormDataMapped<T>;
  }) => DocsFormDataMapped<T>;
  sections: {
    position: number;
    title: (props: { translations: Translations }) => string;
    description?: (props: { translations: Translations }) => string;
    fields: Partial<
      Record<
        keyof DocsFormDataMapped<T>,
        {
          subTitle?: (props: { translations: Translations }) => string;
          input: (props: FormFieldConfigProps<T>) => ReactNode;
          position: number;
        }
      >
    >;
  }[];
};

type FormFieldConfigs = Record<
  MigratedFormTypes,
  (props: {
    translations: Translations;
    skipRequiredValidation: boolean;
  }) => FormFieldConfig<MigratedFormTypes>
>;

const FORM_FIELD_CONFIGS: FormFieldConfigs = {
  [REHAB_FORM_NAMES.GKV_GENERAL]: getGKVGeneralFormConfig,
};

export const useGetFormFieldConfig = (
  formName: MigratedFormTypes,
  translations: Translations,
): {
  config: FormFieldConfig<MigratedFormTypes>;
  skipRequiredValidation: boolean;
} => {
  const { careseeker } = useSenderAccountContext();
  const isDtaActivated = getIsDtaActive({ careseeker });

  const skipRequiredValidation =
    !isDtaActivated || formName !== REHAB_FORM_NAMES.GKV_GENERAL;

  return {
    config: FORM_FIELD_CONFIGS[formName]({
      skipRequiredValidation,
      translations,
    }),
    skipRequiredValidation,
  } as const;
};
